
import { FormGroup, Input, Label, Tooltip } from "reactstrap";
import { useThemeContext } from "../../Context/ThemeContext";
import { getDarkDropdown } from "../../Utils/otherFunctions";
import { forwardRef, useState, useEffect } from "react";
import { Info } from "react-feather";

const RadioSelectField = forwardRef(function RadioSelectField(props, ref) {
  const { 
    label, 
    options, 
    fieldState, 
    className, 
    withTextInput = false,
    textInputOption = null,
    textInputName = "",
    textInputValue = "",
    textInputPlaceholder = "Masukkan detail lainnya",
    onTextInputChange = () => {},
    disabled = false, // Add explicit disabled prop with default value
  } = props;
  
  const { themeContext } = useThemeContext();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  
  const toggle = () => setTooltipOpen(!tooltipOpen);
  const isOptionsEmpty = options?.length <= 1;

  // Check if we need to show the text input based on the selected value
  useEffect(() => {
    if (withTextInput && textInputOption) {
      const selectedValue = props.value;
      setShowTextInput(selectedValue === textInputOption);
    }
  }, [props.value, withTextInput, textInputOption]);

  // Handle radio button change
  const handleRadioChange = (e) => {
    // If component is disabled, don't allow changes
    if (disabled || props.disabled) {
      return;
    }
    
    // Call the original onChange handler
    if (props.onChange) {
      props.onChange(e);
    }
    
    // Update the text input visibility
    if (withTextInput && textInputOption) {
      setShowTextInput(e.target.value === textInputOption);
    }
  };

  // Handle text input change while respecting disabled state
  const handleTextInputChange = (e) => {
    if (!disabled && !props.disabled) {
      onTextInputChange(e);
    }
  };

  return (
    <FormGroup className={className}>
      {label && (
        <Label className={`bg-transparent ${disabled || props.disabled ? 'text-muted' : ''}`}>
          {label}{" "}
          {props.isRequired && (
            <font className="ml-1" color={disabled || props.disabled ? "grey" : "red"}>
              *
            </font>
          )}
          {props.tooltip && <Info id={`tooltip-${props.name}`} size={10} />}
        </Label>
      )}

      <div className={`radio-options-container ${disabled || props.disabled ? 'opacity-75' : ''}`}>
        {props.placeholder && (
          <div className={"d-flex align-items-center mb-2"}>
            <Input
              className={`btn-dark m-0 ${
                (isOptionsEmpty || disabled || props.disabled) && themeContext === "dark" && "bg-dark"
              } ${getDarkDropdown(themeContext)}`}
              disabled={isOptionsEmpty || disabled || props.disabled}
              invalid={fieldState?.error && !disabled && !props.disabled}
              type="radio"
              ref={ref}
              {...props}
              onChange={handleRadioChange}
              value={null}
              defaultChecked={props.defaultChecked == null}
              style={{ cursor: (disabled || props.disabled) ? 'not-allowed' : 'pointer' }}
            />
            <Label 
              className={`m-0 px-2 ${disabled || props.disabled ? 'text-muted' : ''}`}
              style={{ cursor: (disabled || props.disabled) ? 'not-allowed' : 'pointer' }}
            >
              {props.placeholder}
            </Label>
          </div>
        )}

        {options.map((option) => (
          <div key={option.id}>
            <div className={"d-flex align-items-center mb-2"}>
              <Input
                className={`m-0 ${getDarkDropdown(themeContext)} ${props.value === (option.value || option.id) ? 'btn-dark' : ''}`}
                disabled={isOptionsEmpty || disabled || props.disabled || option.disabled}
                invalid={fieldState?.error && !disabled && !props.disabled}
                type="radio"
                ref={ref}
                {...props}
                onChange={handleRadioChange}
                value={option.value || option.id}
                checked={props.value === (option.value || option.id)}
                defaultChecked={props.defaultChecked == option.id}
                style={{ cursor: (disabled || props.disabled) ? 'not-allowed' : 'pointer' }}
              />
              <Label 
                className={`m-0 px-2 ${disabled || props.disabled ? 'text-muted' : ''}`}
                style={{ cursor: (disabled || props.disabled) ? 'not-allowed' : 'pointer' }}
              >
                {option.name}
              </Label>
            </div>
            
            {/* If this is the option that can have additional text input and it's selected */}
            {withTextInput && 
              textInputOption === (option.value || option.id) &&
              showTextInput && (
                <div className="mb-3 ml-4">
                  <Input
                    type="text"
                    name={textInputName}
                    value={textInputValue}
                    onChange={handleTextInputChange}
                    placeholder={textInputPlaceholder}
                    className="form-control"
                    disabled={disabled || props.disabled}
                  />
                </div>
            )}
          </div>
        ))}
      </div>

      {fieldState?.error && !disabled && !props.disabled && (
        <Label className={"label-text text-danger"}>
          {fieldState.error.message}
        </Label>
      )}

      {props.tooltip && (
        <Tooltip
          placement="top"
          isOpen={tooltipOpen}
          target={`tooltip-${props.name}`}
          toggle={toggle}
        >
          {props.tooltip}
        </Tooltip>
      )}

      <style jsx>{`
        .radio-options-container {
          display: flex;
          flex-direction: column;
        }
        
        .opacity-75 {
          opacity: 0.75;
        }
      `}</style>
    </FormGroup>
  );
});

export default RadioSelectField;